import { FC, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DonutChart, Sparkline } from "@mantine/charts";
import { postRequestHigherCreditLimit, useGetCompanyCreditById } from "@/services/Company";
import { useGetPeriodicSales, useGetPeriodicBuys } from "@/services/Graphs";
import { CurrencyCode } from "@/types";
import { Divider, Grid, Group, Stack } from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { SNAPSHOT_TASK_COLUMNS } from "@/components/organisms/InvoiceTable/const/snapshot";
import { SNAPSHOT_ORDER_COLUMNS } from "../../organisms/OrdersTable/const/snapshot";
import { useAuth } from "@/contexts/AuthContext";
import { useGetDashboardStatistics } from "@/services/Statistics";
import Modal, { ModalCTAs } from "../../molecules/Modal";

import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import dayjs from "dayjs";
import Spinner from "@/components/atoms/Spinner/Spinner";
import SelectCompanyOnLogin from "@/components/molecules/SelectInputs/LoginCompanySelect/LoginCompanySelectHelper";

import TextInput from "@/components/atoms/TextInput";
import OrdersTable from "@/components/organisms/OrdersTable";
import TasksTable from "@/components/organisms/TasksTable";
import SettlementTable from "@/components/organisms/SettlementTable";
import InvoiceTable from "@/components/organisms/InvoiceTable";
import NumberFormatter from "@/components/atoms/NumberFormatter";

import styles from "./styles.module.css";
import Heading from "@/components/atoms/Heading";
import { SuccessNotificationObj } from "@/utils/NotificationUtil";
import { notifications } from "@mantine/notifications";

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <>
      <title>{`Reviver - ${t("dashboard:dashboard")}`}</title>
      <meta name="description" content={`Reviver - ${t("dashboard:dashboard")}`} />
      <div className={styles.dashboardRoot}>
        <div className={styles.dashboardContent}>
          <KPIsnapshot />
          <Group justify="flex-end">
            <CTA intent="secondary" onClick={() => navigate("/reports")}>
              {t("dashboard:seeAllReports")}
            </CTA>
          </Group>
          <TaskSnapshot />
          <FinancialSnapshot />
          <OrderSnapshot />
        </div>
      </div>
      <SelectCompanyOnLogin />
    </>
  );
}

const KPIsnapshot: FC = () => {
  const { t } = useTranslation();
  const selectedPeriod = { label: t("dashboard:30days"), value: 30 };
  // const [selectedPeriod, setSelectedPeriod] = useState<DropdownOption>({
  //   label: t("dashboard:30days"),
  //   value: 30,
  // });

  // const periods = [
  //   { label: t("dashboard:7days"), value: 7 },
  //   { label: t("dashboard:14days"), value: 14 },
  //   { label: t("dashboard:30days"), value: 30 },
  //   { label: t("dashboard:60days"), value: 60 },
  //   { label: t("dashboard:90days"), value: 90 },
  // ];

  const { data: dashboardAmountStatistics } = useGetDashboardStatistics(
    dayjs().subtract(selectedPeriod.value, "days").format(),
    dayjs().format()
  );

  return (
    <Stack gap={"14px"}>
      <Group justify="space-between">
        <Heading order={1} styleOrder={4} strong>
          {t(`dashboard:lastXDays`, { days: selectedPeriod.value })}
        </Heading>
        {/*<Dropdown*/}
        {/*  dropDownClassNames={styles.periodDropdown}*/}
        {/*  id="period-dropdown"*/}
        {/*  defaultValue={periods[0]}*/}
        {/*  options={periods}*/}
        {/*  value={selectedPeriod}*/}
        {/*  onChange={(e) => setSelectedPeriod(e)}*/}
        {/*/>*/}
      </Group>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
          <PurchaseCard period={selectedPeriod.value} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
          <SalesCard period={selectedPeriod.value} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
          <Card>
            <Group justify="space-between" align="end">
              <Text size="lg" strong>{`${t("dashboard:tonsBought")}`}</Text>
              <Text
                className={styles.cardValue}
              >{`${dashboardAmountStatistics ? dashboardAmountStatistics.numberOfTonsBought : "-"}`}</Text>
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
          <Card>
            <Group justify="space-between" align="end">
              <Text size="lg" strong>{`${t("dashboard:tonsSold")}`}</Text>
              <Text
                className={styles.cardValue}
              >{`${dashboardAmountStatistics ? dashboardAmountStatistics.numberOfTonsSold : "-"}`}</Text>
            </Group>
          </Card>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

const FinancialSnapshot: FC = () => {
  return (
    <Grid>
      <Grid.Col span={{ base: 12, lg: 2 }}>
        <Credit />
      </Grid.Col>
      <Grid.Col span={{ base: 12, lg: 5 }}>
        <SettlementSnapshot />
      </Grid.Col>
      <Grid.Col span={{ base: 12, lg: 5 }}>
        <InvoiceSnapshot />
      </Grid.Col>
    </Grid>
  );
};

const TaskSnapshot: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleRedirectToTasksPage = () => {
    navigate("/task");
  };

  return (
    <>
      <Group justify="space-between">
        <Group align="end">
          <Heading order={2} styleOrder={4} strong>
            {t("common:tasks")}
          </Heading>
        </Group>
        <CTA intent="secondary" onClick={handleRedirectToTasksPage}>
          {t("dashboard:seeAllTasks")}
        </CTA>
      </Group>
      <TasksTable
        visibleColumns={SNAPSHOT_TASK_COLUMNS}
        initialSort={[{ desc: false, id: "dueDate" }]}
        pagination={false}
        actionable={true}
        initialRowsPerPage={5}
      />
    </>
  );
};

interface PurchaseAndPurchaseCardProps {
  period: number;
}
const PurchaseCard: FC<PurchaseAndPurchaseCardProps> = (props) => {
  const { t } = useTranslation();
  const { data: buyData, isLoading } = useGetPeriodicBuys(
    dayjs().subtract(props.period, "days").format(),
    dayjs().format(),
    props.period,
    true,
    {
      enabled: props.period > 0,
    }
  );

  const { currentCompany } = useAuth();
  const totalBuy = buyData?.reduce((sum, opt) => sum + Number(opt.value), 0) || 0;

  return (
    <Card>
      <Group justify="space-between" align="end">
        <Group>
          <SVGAsset style={{ height: "36px" }} name="purchaseIndicatorLarge" />
          <Text size="lg" color="success" strong>
            {t("dashboard:purchased")}
          </Text>
        </Group>
        <Stack align="center">
          {isLoading ? (
            <Spinner loading={isLoading} inline />
          ) : (
            <Sparkline
              w="135"
              h="35"
              color="var(--color-green-100)"
              fillOpacity={0}
              data={buyData ? buyData.map((x) => x.value) : [0, 0]}
            />
          )}
          <Text display="technical" color="success">
            {totalBuy.toFixed(2)} {currentCompany?.companyCurrency}
          </Text>
        </Stack>
      </Group>
    </Card>
  );
};

const SalesCard: FC<PurchaseAndPurchaseCardProps> = (props) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();

  const { data: salesData, isLoading } = useGetPeriodicSales(
    dayjs().subtract(props.period, "days").format(),
    dayjs().format(),
    props.period,
    true,
    {
      enabled: props.period > 0,
    }
  );
  const totalSales = salesData ? salesData.reduce((acc, curr) => acc + curr.value, 0) : 0;

  return (
    <Card>
      <Group justify="space-between" align="end">
        <Group>
          <SVGAsset style={{ height: "36px" }} name="salesIndicatorLarge" />
          <Text size="lg" color="error">
            {t("dashboard:sold")}
          </Text>
        </Group>
        <Stack align="center">
          {isLoading ? (
            <Spinner loading={isLoading} inline />
          ) : (
            <Sparkline
              w="135"
              h="35"
              color="var(--color-red-100)"
              fillOpacity={0}
              data={salesData ? salesData.map((x) => x.value) : [0, 0]}
            />
          )}
          <Text display="technical" color="error">
            {totalSales.toFixed(2)} {currentCompany?.companyCurrency}
          </Text>
        </Stack>
      </Group>
    </Card>
  );
};

const Credit: FC = () => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [creditLimit, setCreditLimit] = useState<string>();

  const { data: companyCredit } = useGetCompanyCreditById(currentCompany?.companyId);

  const {
    mutateAsync: mutateRequestHigherCreditLimit,
    isPending: isRequestHigherCreditLimitPending,
    isSuccess: isRequestHigherCreditLimitSuccess,
    isError: isRequestHigherCreditLimitError,
    reset: resetRequestHigherCreditLimit,
  } = useMutation({
    mutationFn: async () => {
      await postRequestHigherCreditLimit(currentCompany?.companyId as number, {
        requestedCredit: Number(creditLimit),
        currency: currentCompany?.companyCurrency as CurrencyCode,
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        onCloseModal();
      }, 2500);
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.saveSuccess"),
          t("notifications:creditApplication.applicationApprovedSuccess")
        )
      );
    },
    onError: () => {
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.saveError"),
          t("notifications:creditApplication.applicationApprovedError")
        )
      );
    },
  });

  function onCloseModal() {
    setIsModalOpen(false);
    resetRequestHigherCreditLimit();
  }

  const creditConverted = [
    {
      name: "Current credit",
      value: companyCredit ? companyCredit.creditLimit - companyCredit.remainingCredit : 0,
      color: "#2A2A2A",
    },
    {
      name: "Max credit",
      value: companyCredit ? companyCredit.creditLimit : 0,
      color: "#CACACA",
    },
  ];

  return (
    <>
      <Stack gap={"14px"}>
        <Heading order={2} styleOrder={4} strong>
          {t("common:credit")}
        </Heading>
        <Card>
          <Stack align="center">
            {companyCredit ? <DonutChart withTooltip={false} data={creditConverted} /> : null}
            <Group>
              <Text color="primary">
                <NumberFormatter
                  value={companyCredit?.remainingCredit}
                  suffix={` ${currentCompany?.companyCurrency}`}
                />
              </Text>
              <Text style={{ color: "var(--color-brand-shamrock-100)" }}>/</Text>
              <Text color="secondary">
                <NumberFormatter value={companyCredit?.creditLimit} suffix={` ${currentCompany?.companyCurrency}`} />
              </Text>
            </Group>
            <CTA intent="secondary" onClick={() => setIsModalOpen(true)}>
              {t("dashboard:increaseCreditLimit")}
            </CTA>
          </Stack>
        </Card>
      </Stack>

      <Modal
        size={"lg"}
        opened={isModalOpen}
        onClose={function (): void {
          onCloseModal();
        }}
        title={t("dashboard:applyForHigherCreditLimit")}
      >
        <Stack>
          <Group
            pt="xl"
            pb={isRequestHigherCreditLimitError || isRequestHigherCreditLimitSuccess ? "0" : "xl"}
            justify="center"
          >
            <Stack align="end">
              <Text size="sm" color="secondary">
                {t("dashboard:currentCreditLimit")}
              </Text>
              <Text size="sm">
                {companyCredit?.creditLimit} {currentCompany?.companyCurrency}
              </Text>
            </Stack>
            <Divider orientation="vertical" />
            <TextInput
              label={t("dashboard:newCreditLimit")}
              type="number"
              value={creditLimit}
              onChange={(e) => setCreditLimit(e.currentTarget.value)}
            />
          </Group>
        </Stack>

        <ModalCTAs>
          <CTA intent="secondary" onClick={() => onCloseModal()}>
            {t("actions:cancel")}
          </CTA>
          <CTA onClick={() => mutateRequestHigherCreditLimit()} loading={isRequestHigherCreditLimitPending}>
            {t("common:apply")}
          </CTA>
        </ModalCTAs>
      </Modal>
    </>
  );
};

const SettlementSnapshot: FC = () => {
  const { t } = useTranslation();

  return (
    <Stack gap={"14px"}>
      <Group align="end">
        <Heading order={2} styleOrder={4} strong>
          {t("dashboard:settlements")}
        </Heading>
      </Group>
      <SettlementTable pagination={false} />
    </Stack>
  );
};

const InvoiceSnapshot: FC = () => {
  const { t } = useTranslation();

  return (
    <Stack gap={"14px"}>
      <Group align="end">
        <Heading order={2} styleOrder={4} strong>
          {t("dashboard:invoices")}
        </Heading>
      </Group>
      <InvoiceTable pagination={false} initialRowsPerPage={5} />
    </Stack>
  );
};

const OrderSnapshot: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleRedirectToTradePage = () => {
    navigate("/myOrders");
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Heading order={2} styleOrder={4} strong>
          {t("common:orders")}
        </Heading>
        <CTA intent="secondary" onClick={handleRedirectToTradePage}>
          {t("dashboard:seeAllOrders")}
        </CTA>
      </Group>
      <OrdersTable visibleColumns={SNAPSHOT_ORDER_COLUMNS} pagination={false} initialRowsPerPage={5} />
    </Stack>
  );
};

interface CardProps {
  children: ReactNode;
}
const Card: FC<CardProps> = ({ children }) => {
  return <div className={styles.cardRoot}>{children}</div>;
};
